import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, throwError } from 'rxjs';
import { catchError, finalize, first, map, mergeMap, shareReplay } from 'rxjs/operators';
import { ErrorService, LogService, UrlHelperService } from '../../core';
import { AUTH_CONFIG, AuthConfig } from '../config/auth.config';
import { HttpEndpointsConstants } from '../constants/http-endpoints.constants';
import { AuthResponseInterface } from '../interfaces/auth-response.interface';
import { AuthStateEnum } from '../interfaces/auth-state.enum';
import { UnknownType } from '../interfaces/unknown.type';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class BearerTokenAuthInterceptor implements HttpInterceptor {
  public static readonly RETRY_FAILED_STATUS_CODE: number = 429;

  // TODO refactor use environment for blacklist
  private endpointBlacklist: (HttpEndpointsConstants | string)[] = [
    HttpEndpointsConstants.login,
    '/assets/',
    '/es/',
    '/translation/',
    '/cms/',
  ];

  private endpointRefreshBlacklist: HttpEndpointsConstants[] = [HttpEndpointsConstants.refresh, HttpEndpointsConstants.logout];

  /**
   * Shared token-refresh attempt for all requests that receive a 401 at the same time.
   * Ensures a single `auth/refresh-token` call and a single logout (and therefore a single
   * "logout successful" toast) when refreshing fails, instead of one per in-flight request.
   */
  private refresh$: Observable<AuthStateEnum> | null = null;

  constructor(
    @Inject(AUTH_CONFIG) private config: AuthConfig,
    private authService: AuthService,
    private errorService: ErrorService,
    private router: Router,
    private log: LogService,
    private urlHelper: UrlHelperService,
  ) {}

  public intercept(request: HttpRequest<UnknownType>, next: HttpHandler): Observable<HttpEvent<UnknownType>> {
    const isNotBlacklisted: boolean = request.url.match(this.endpointBlacklist.join('|')) === null;
    if (!isNotBlacklisted || request.method.toLowerCase() === 'options') {
      return next.handle(request);
    }

    const isNotRefreshBlacklisted: boolean = request.url.match(this.endpointRefreshBlacklist.join('|')) === null;
    return this.addAuth(request).pipe(
      first(),
      mergeMap((authRequest: HttpRequest<UnknownType>) => next.handle(authRequest)),
      catchError((errorResponse: HttpErrorResponse) => {
        this.log.warn('BearerTokenAuthInterceptor.intercept()', errorResponse);

        if (errorResponse.status === 401 && isNotRefreshBlacklisted) {
          return this.refreshTokenRequest(request, next);
        }

        throw errorResponse;
      }),
    );
  }

  private addAuth(request: HttpRequest<UnknownType>): Observable<HttpRequest<UnknownType>> {
    const headers: HttpHeaders = request.headers;

    return this.authService.getToken().pipe(
      // tap((token: AuthResponseInterface | undefined) => this.log.warn('addAuth', token)),
      first(),
      map((token: AuthResponseInterface | undefined) =>
        request.clone(token?.token ? { headers: headers.set('Authorization', `Bearer ${token.token}`) } : { headers }),
      ),
    );
  }

  private refreshTokenRequest(request: HttpRequest<UnknownType>, next: HttpHandler): Observable<HttpEvent<UnknownType>> {
    return this.getRefresh().pipe(
      first(),
      mergeMap(() => this.addAuth(request)),
      mergeMap((authRequest: HttpRequest<UnknownType>) => next.handle(authRequest)),
      catchError((refreshErrorResponse: HttpErrorResponse) => {
        throw { ...refreshErrorResponse, status: BearerTokenAuthInterceptor.RETRY_FAILED_STATUS_CODE };
      }),
    );
  }

  /**
   * Returns a single, shared refresh-token attempt. All requests that hit a 401 while a refresh is
   * in flight subscribe to the same stream, so the token is refreshed only once and — on failure —
   * logout (and its toast) is triggered only once.
   */
  private getRefresh(): Observable<AuthStateEnum> {
    if (!this.refresh$) {
      this.log.warn('refresh');
      this.refresh$ = from(this.authService.refresh()).pipe(
        catchError((refreshErrorResponse: HttpErrorResponse) => {
          // logout once when refreshing fails for the whole batch of failed requests
          this.authService
            .logout(true)
            .then(() => this.router.navigate([this.urlHelper.localizeUrl(this.config.unauthorizedRoute)]))
            .catch((error: Error) =>
              this.errorService.add({
                label: 'BearerTokenAuthInterceptor.getRefresh()',
                ...error,
                error,
              }),
            );
          return throwError(() => refreshErrorResponse);
        }),
        finalize(() => (this.refresh$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refresh$;
  }
}
